import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import { commitTimezoneChange, registerTimezoneCacheListeners } from './timezoneCache';

export const meSchema = z.object({
  user_id: z.string().uuid(),
  email: z.string().email().nullable(),
  timezone: z.string(),
});

export type Me = z.infer<typeof meSchema>;
export type MeUpdate = { email?: string; timezone?: string };

function useTimezoneCacheListeners(queryClient: ReturnType<typeof useQueryClient>): void {
  useEffect(() => registerTimezoneCacheListeners(queryClient), [queryClient]);
}

export function useMe() {
  const queryClient = useQueryClient();
  useTimezoneCacheListeners(queryClient);
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const response = await client.get('/api/users/me');
      return parseEnvelope(meSchema, response.data).data;
    },
  });
}

export function useUpdateMe() {
  const queryClient = useQueryClient();
  useTimezoneCacheListeners(queryClient);
  return useMutation({
    mutationFn: async (input: MeUpdate) => {
      const body = Object.fromEntries(Object.entries(input).filter(([, value]) => value != null));
      const response = await client.patch('/api/users/me', body);
      return parseEnvelope(meSchema, response.data).data;
    },
    onMutate: () => queryClient.getQueryData<Me>(['me'])?.timezone,
    onSuccess: async (me, _input, oldTimezone) => {
      queryClient.setQueryData(['me'], me);
      await commitTimezoneChange(queryClient, oldTimezone, me.timezone);
    },
  });
}
